/**
 * The tender plan as the portal shows it: how full an allowance is, and how a plan-limit refusal
 * reads. Pure, so the thresholds are tested apart from the components that paint them.
 *
 * Two kinds of allowance, and they behave differently (server contract,
 * tri-server `founder/tender-pricing-spec.md`):
 * - **soft** (analyses, concepts): never blocked — beyond the allowance the overage is billed.
 * - **hard** (search profiles, users): creating one more is refused with a 409.
 */
import type { TenderPlanLimitError } from "./general/Errors";
import type { TenderPlanAllowance } from "./TenderSearchProfileAPI";

/** The slice of i18next's `t` used here. The options-object form is the one react-i18next's
 *  overloaded `TFunction` is assignable to (a positional fallback string is not). */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** From 80 % of an allowance on, the panel warns. */
export const TENDER_PLAN_WARNING_RATIO = 0.8;

export type AllowanceLevel =
  /** `included: null` — no limit. */
  | "unlimited"
  /** `included: 0` and nothing used — the plan does not contain it (concepts on Radar). */
  | "notIncluded"
  | "normal"
  /** 80 % or more used, still within the allowance. */
  | "warning"
  /** Soft allowance exceeded — the overage is billed, nothing is blocked. */
  | "over"
  /** Hard allowance used up — the next one is refused. */
  | "full";

export function allowanceLevel(allowance: TenderPlanAllowance, hard: boolean): AllowanceLevel {
  const { used, included } = allowance;
  if (included === null || included === undefined) return "unlimited";
  if (included <= 0) {
    if (used <= 0) return "notIncluded";
    return hard ? "full" : "over";
  }
  if (hard ? used >= included : used > included) return hard ? "full" : "over";
  if (used / included >= TENDER_PLAN_WARNING_RATIO) return "warning";
  return "normal";
}

/** The plan's display name; an unknown tier is shown as it came. */
export function tenderTierLabel(tier: string | null | undefined, t: Translate): string {
  if (!tier) return "";
  const key = `tenderPlan.tier.${tier}`;
  const label = t(key);
  return label && label !== key ? label : tier;
}

/** Title and text of the dialog shown when the server refuses a profile or user over the plan. */
export function tenderPlanLimitMessage(
  error: TenderPlanLimitError,
  t: Translate
): { title: string; message: string } {
  const tier = tenderTierLabel(error.tier, t);
  return {
    title: t("tenderPlan.limit.title"),
    message: t(`tenderPlan.limit.${error.limit}`, {
      tier,
      used: error.used,
      included: error.included ?? "–",
    }),
  };
}
